<script setup lang="ts">
/**
 * TurnInput — input controlado para turno do aprendiz.
 * Emite `submit` quando Enter é pressionado (sem Shift).
 *
 * Fase 0 (Lume) F3: input por voz (STT) opcional.
 * Fase 1 (Lume) A3, A4, A7: anexos (imagem, sketch, paste de URL).
 *
 * Constitucional:
 * - CC-06: botão explícito "Enviar" (sem confirm-shaming).
 * - CL-08: anexos ficam em memória; nada sai do device sem decisão do pai.
 * - P-UX-02 (One thing at a time): uma escolha por vez, sem modal paralelo.
 * - Sem placeholder manipulativo.
 */
import { ref, computed, watch, nextTick } from "vue";
import { useAccessibility } from "../../accessibility/composables/useAccessibility";
import { useVoiceInput } from "../../accessibility/composables/useVoiceInput";
import ImageAttachment from "../../multimodal/components/ImageAttachment.vue";
import Sketchpad from "../../multimodal/components/Sketchpad.vue";
import type { Attachment } from "../../multimodal/types";

interface Props {
  disabled?: boolean;
}

interface Emits {
  (e: "submit", text: string, attachments: Attachment[]): void;
}

const props = withDefaults(defineProps<Props>(), { disabled: false });
const emit = defineEmits<Emits>();

const a11y = useAccessibility();
const draft = ref<string>("");
const attachments = ref<Attachment[]>([]);
const showSketchpad = ref(false);
const showImagePicker = ref(false);
const showVoiceError = ref<string | null>(null);
const showImageError = ref<string | null>(null);
// HLOS-323: status da análise Vision (visível quando envia com imagem)
const isAnalyzing = ref(false);

const canSubmit = computed(() => {
  return draft.value.trim().length > 0 || attachments.value.length > 0;
});

// HLOS-374 inv-B: ref do textarea para gerenciar foco via microfone.
const textareaRef = ref<HTMLTextAreaElement | null>(null);

const voice = useVoiceInput();

// HLOS-374 inv-B: live update do textarea enquanto usuário fala.
// Cada partial chunk (isFinal=false) atualiza draft em tempo real.
// O chunk final (isFinal=true) também atualiza — pode haver refino de pontuação.
watch(
  () => voice.transcript.value,
  (newText: string | undefined) => {
    const text = newText ?? "";
    if (voice.isListening.value || text.trim().length > 0) {
      // Substitui (não concatena) o texto do usuário com a transcrição
      // enquanto está escutando. Quando para, a transcrição final fica.
      draft.value = text;
    }
  },
  { flush: "post" },
);

// HLOS-374 inv-B: foco do textarea + state mgmt do ditado.
// Quando isListening vira true: foca textarea (cursor no campo de digitação).
// Quando vira false: mantém foco (user pode editar imediatamente).
watch(
  () => voice.isListening.value,
  async (isListening, wasListening) => {
    if (isListening) {
      await nextTick();
      textareaRef.value?.focus();
    } else if (wasListening) {
      // Listening acabou — limpa erro visual e mantém foco.
      showVoiceError.value = null;
    }
  },
);

watch(voice.error, (e) => {
  if (e) {
    const translated = translateVoiceError(e);
    // Se for null (aborted), mantém o estado anterior em vez de limpar
    showVoiceError.value = translated ?? showVoiceError.value;
  } else {
    showVoiceError.value = null;
  }
});

/**
 * Traduz códigos de erro do Web Speech API para mensagens úteis
 * em PT-BR, com guidance específico para iOS.
 *
 * Códigos:
 * - "not-allowed" / "service-not-allowed": permissão negada (iOS)
 * - "no-speech": nenhum som detectado
 * - "audio-capture": microfone indisponível (sem hardware)
 * - "network": necessário online para alguns reconhecedores
 * - "aborted": usuário cancelou (não exibe mensagem)
 * - "no-results": HLOS-319 — iOS WKWebView iniciou mas não transcreveu
 *   (limitação conhecida do Web Speech API no WKWebView)
 */
function translateVoiceError(code: string): string | null {
  switch (code) {
    case "aborted":
      return null;
    case "not-allowed":
    case "service-not-allowed":
      return "Permissão do microfone negada. Você pode permitir em Ajustes > Lume > Microfone, ou continuar digitando.";
    case "no-speech":
      return "Nenhuma fala detectada. Tente novamente quando estiver pronto.";
    case "audio-capture":
      return "Microfone indisponível neste dispositivo. Você pode continuar digitando.";
    case "network":
      return "Reconhecimento de voz precisa de conexão. Verifique sua internet ou continue digitando.";
    case "unsupported":
      return "Reconhecimento de voz não é suportado neste contexto. Você pode continuar digitando.";
    case "no-results":
      return "O navegador não transcreveu (limitação do Web Speech API no iOS). Você pode continuar digitando.";
    case "recognition-failed":
      return "Reconhecimento de voz falhou. Você pode continuar digitando.";
    default:
      return `Não foi possível iniciar a ditada (${code}). Você pode continuar digitando.`;
  }
}

function onSubmit(): void {
  if (props.disabled) return;
  if (!canSubmit.value) return;
  // HLOS-323: indica que o Vision está analisando enquanto o pai processa.
  // A flag é resetada quando o pai termina (via onSubmitted callback).
  if (attachments.value.length > 0) {
    isAnalyzing.value = true;
  }
  emit("submit", draft.value.trim(), [...attachments.value]);
  draft.value = "";
  attachments.value = [];
  showSketchpad.value = false;
  showImagePicker.value = false;
}

defineExpose({
  resetAnalyzing: () => {
    isAnalyzing.value = false;
  },
});

function onKeydown(ev: KeyboardEvent): void {
  if (props.disabled) return;
  if (ev.key === "Enter" && !ev.shiftKey) {
    ev.preventDefault();
    onSubmit();
  }
}

function toggleVoice(): void {
  if (voice.isListening.value) {
    voice.stop();
  } else {
    voice.start("pt-BR");
  }
}

function onImageAttach(dataUrl: string, filename: string) {
  attachments.value.push({
    id: crypto.randomUUID(),
    kind: "image",
    dataUrl,
    filename,
    attachedAt: Date.now(),
  });
  showImagePicker.value = false;
  // HLOS-319: limpar erros antigos (de voz ou de imagem anterior)
  // para que o erro não 'vaze' para esta nova ação.
  showVoiceError.value = null;
  showImageError.value = null;
}

function onImageError(message: string) {
  showImageError.value = message;
}

function onSketchSave(dataUrl: string) {
  attachments.value.push({
    id: crypto.randomUUID(),
    kind: "sketch",
    dataUrl,
    filename: `sketch-${Date.now()}.png`,
    attachedAt: Date.now(),
  });
  showSketchpad.value = false;
}

function onSketchCancel() {
  showSketchpad.value = false;
}

function removeAttachment(id: string) {
  attachments.value = attachments.value.filter((a) => a.id !== id);
}
</script>

<template>
  <form class="hlos-turn-input" @submit.prevent="onSubmit" data-testid="turn-input">
    <!-- Anexos já adicionados (preview) -->
    <ul
      v-if="attachments.length > 0"
      class="hlos-turn-input__attachments"
      aria-label="Anexos do turno"
    >
      <li
        v-for="att in attachments"
        :key="att.id"
        class="hlos-turn-input__attachment"
      >
        <img
          v-if="att.kind === 'image' || att.kind === 'sketch'"
          :src="att.dataUrl"
          :alt="att.filename ?? 'anexo'"
          class="hlos-turn-input__attachment-img"
        />
        <span class="hlos-turn-input__attachment-label">{{ att.filename }}</span>
        <button
          type="button"
          class="hlos-turn-input__attachment-remove"
          :aria-label="`Remover ${att.filename}`"
          @click="removeAttachment(att.id)"
        >
          ×
        </button>
      </li>
    </ul>

    <!-- A3: anexo de imagem (file picker + drag/drop + paste) -->
    <ImageAttachment
      v-if="showImagePicker"
      @attach="onImageAttach"
      @error="onImageError"
    />

    <!-- A4: sketchpad -->
    <Sketchpad
      v-if="showSketchpad"
      @save="onSketchSave"
      @cancel="onSketchCancel"
    />

    <label for="hlos-turn-input-field" class="sr-only">Seu turno</label>
    <textarea
      id="hlos-turn-input-field"
      ref="textareaRef"
      v-model="draft"
      :disabled="props.disabled"
      placeholder="Escreva aqui…"
      rows="2"
      :aria-describedby="showVoiceError ? 'turn-input-voice-error' : undefined"
      @keydown="onKeydown"
      data-testid="turn-input-field"
    />
    <div class="hlos-turn-input__row">
      <div class="hlos-turn-input__tools">
        <button
          type="button"
          class="hlos-turn-input__tool"
          :aria-pressed="showImagePicker"
          :aria-label="showImagePicker ? 'Fechar anexo de imagem' : 'Anexar imagem'"
          data-testid="turn-input-image"
          @click="showImagePicker = !showImagePicker"
        >
          <span aria-hidden="true">📎</span>
        </button>
        <button
          type="button"
          class="hlos-turn-input__tool"
          :aria-pressed="showSketchpad"
          :aria-label="showSketchpad ? 'Fechar sketchpad' : 'Abrir sketchpad'"
          data-testid="turn-input-sketch"
          @click="showSketchpad = !showSketchpad"
        >
          <span aria-hidden="true">✎</span>
        </button>
        <button
          v-if="a11y.preferences.value.voiceInputEnabled"
          type="button"
          class="hlos-turn-input__tool"
          :class="{ 'hlos-turn-input__tool--listening': voice.isListening.value }"
          :aria-pressed="voice.isListening.value"
          :aria-label="
            voice.isListening.value ? 'Parar de ditar' : 'Ditar por voz'
          "
          data-testid="turn-input-voice"
          @click="toggleVoice"
        >
          <span aria-hidden="true">{{ voice.isListening.value ? "■" : "🎙" }}</span>
        </button>
      </div>
      <button
        type="submit"
        :disabled="props.disabled || !canSubmit"
        data-testid="turn-input-submit"
      >
        Enviar
      </button>
    </div>
    <p
      v-if="showVoiceError"
      id="turn-input-voice-error"
      class="hlos-turn-input__error"
      role="alert"
    >
      {{ showVoiceError }}
    </p>
    <p
      v-if="showImageError"
      class="hlos-turn-input__error"
      role="alert"
    >
      {{ showImageError }}
    </p>
    <p
      v-if="voice.isListening.value"
      class="hlos-turn-input__status"
      role="status"
      aria-live="polite"
    >
      <span aria-hidden="true">🎙</span>
      Ouvindo… (se nada aparecer, pare e digite — limitação do navegador no iOS)
    </p>
    <p
      v-if="isAnalyzing"
      class="hlos-turn-input__status hlos-turn-input__analyzing"
      role="status"
      aria-live="polite"
      data-testid="turn-input-analyzing"
    >
      <span aria-hidden="true">👁</span>
      Analisando imagem com Vision on-device…
    </p>
  </form>
</template>

<style scoped>
.hlos-turn-input {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 0.5rem 0;
}
textarea {
  font: inherit;
  padding: 0.5rem;
  border: 1px solid color-mix(in srgb, currentColor 25%, transparent);
  border-radius: 0.375rem;
  background: transparent;
  color: inherit;
  resize: vertical;
  min-height: 2.5rem;
}
.hlos-turn-input__row {
  display: flex;
  gap: 0.5rem;
  align-items: center;
  justify-content: space-between;
}
.hlos-turn-input__tools {
  display: flex;
  gap: 0.25rem;
}
.hlos-turn-input__tool {
  font: inherit;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: 1px solid currentColor;
  border-radius: 6px;
  background: transparent;
  color: inherit;
  cursor: pointer;
}
.hlos-turn-input__tool--listening {
  background: var(--hlos-color-error);
  color: var(--hlos-color-bg);
  border-color: var(--hlos-color-error);
}
.hlos-turn-input__tool:hover:not(:disabled) {
  background: var(--hlos-color-hover);
}
button[type="submit"] {
  font: inherit;
  padding: 0.5rem 1rem;
  border: 1px solid currentColor;
  border-radius: 0.375rem;
  background: transparent;
  color: inherit;
  cursor: pointer;
}
button[type="submit"]:hover:not(:disabled) {
  background: color-mix(in srgb, currentColor 10%, transparent);
}
button[type="submit"]:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.hlos-turn-input__error,
.hlos-turn-input__status {
  font-size: 0.85em;
  color: var(--hlos-color-neutral-600);
  margin: 0;
}
.hlos-turn-input__analyzing {
  color: var(--hlos-color-primary);
  font-weight: 500;
}
.hlos-turn-input__error {
  color: var(--hlos-color-error);
}
.hlos-turn-input__attachments {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}
.hlos-turn-input__attachment {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.25rem 0.5rem;
  border: 1px solid var(--hlos-color-neutral-200);
  border-radius: 6px;
  background: var(--hlos-color-bg);
}
.hlos-turn-input__attachment-img {
  width: 32px;
  height: 32px;
  border-radius: 4px;
  object-fit: cover;
}
.hlos-turn-input__attachment-label {
  font-size: 0.85em;
  max-width: 160px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.hlos-turn-input__attachment-remove {
  font: inherit;
  border: none;
  background: transparent;
  cursor: pointer;
  font-size: 1.1em;
  line-height: 1;
  padding: 0 0.25rem;
}
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
</style>
